import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { createLoanApplication, listLoanApplications } from "@/utils/lending.functions";
import { inr, inrPrecise, calcEmi } from "@/lib/lending-format";
import { toast } from "sonner";
import { Calculator } from "lucide-react";

export const Route = createFileRoute("/_authenticated/borrow")({
  head: () => ({ meta: [
    { title: "Borrow — LendyTrack" },
    { name: "description", content: "Apply for a personal loan, calculate EMIs, and track your application." },
  ] }),
  component: BorrowPage,
});

const EMPLOYMENTS = ["Salaried", "Self-employed", "Business", "Student", "Other"] as const;

function BorrowPage() {
  const apps = useQuery({ queryKey: ["loan-apps"], queryFn: () => listLoanApplications() });
  const qc = useQueryClient();
  const submit = useServerFn(createLoanApplication);

  const [amount, setAmount] = useState(150000);
  const [months, setMonths] = useState(24);
  const [rate, setRate] = useState(16);
  const [purpose, setPurpose] = useState("Personal");
  const [income, setIncome] = useState(60000);
  const [employment, setEmployment] = useState<typeof EMPLOYMENTS[number]>("Salaried");
  const [city, setCity] = useState("");

  const emi = useMemo(() => calcEmi(amount, rate, months), [amount, rate, months]);
  const totalPayable = emi * months;
  const totalInterest = totalPayable - amount;

  const m = useMutation({
    mutationFn: () =>
      submit({
        data: {
          amount,
          tenureMonths: months,
          purpose,
          monthlyIncome: income,
          employment,
          city: city || undefined,
        },
      }),
    onSuccess: () => {
      toast.success("Application submitted. We'll review it shortly.");
      qc.invalidateQueries({ queryKey: ["loan-apps"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Borrower</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Apply for a loan</h1>
        <p className="mt-1 text-sm text-muted-foreground">Simulate your EMI first, then submit your application for review.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6 shadow-soft">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-accent">
            <Calculator className="h-3 w-3" /> EMI Calculator
          </div>
          <h2 className="mt-1 font-display text-xl font-semibold">Plan your repayment</h2>

          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <Slider label="Loan amount" value={amount} min={10000} max={2000000} step={5000} onChange={setAmount} format={inr} />
            <Slider label="Tenure (months)" value={months} min={3} max={60} step={3} onChange={setMonths} format={(v: number) => `${v} mo`} />
            <Slider label="Interest rate (% p.a.)" value={rate} min={8} max={24} step={0.25} onChange={setRate} format={(v: number) => `${v}%`} />
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <ResultBox label="Monthly EMI" value={inrPrecise(emi)} highlight />
            <ResultBox label="Total interest" value={inr(totalInterest)} />
            <ResultBox label="Total payable" value={inr(totalPayable)} />
          </div>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); m.mutate(); }}
          className="rounded-xl border border-border bg-card p-6 shadow-soft space-y-3"
        >
          <h2 className="font-display text-lg font-semibold">Application details</h2>
          <Field label="Purpose">
            <input value={purpose} onChange={(e) => setPurpose(e.target.value)} required maxLength={120}
              className="input" />
          </Field>
          <Field label="Monthly income (₹)">
            <input type="number" min={1000} value={income} onChange={(e) => setIncome(+e.target.value)} required className="input" />
          </Field>
          <Field label="Employment">
            <select value={employment} onChange={(e) => setEmployment(e.target.value as any)} className="input">
              {EMPLOYMENTS.map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="City (optional)">
            <input value={city} onChange={(e) => setCity(e.target.value)} maxLength={80} className="input" />
          </Field>
          <button disabled={m.isPending} className="w-full rounded-md bg-primary py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {m.isPending ? "Submitting…" : "Submit application"}
          </button>
        </form>
      </div>

      <h2 className="font-display text-xl font-semibold mt-10 mb-4">My applications</h2>
      {(apps.data ?? []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No applications yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">Purpose</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Tenure</th>
                <th className="px-4 py-3 text-right">Offered EMI</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {(apps.data ?? []).map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="px-4 py-3 font-medium">{a.purpose}</td>
                  <td className="px-4 py-3 text-right">{inr(a.amount)}</td>
                  <td className="px-4 py-3 text-right">{a.tenure_months} mo</td>
                  <td className="px-4 py-3 text-right">{a.emi ? inrPrecise(a.emi) : "—"}</td>
                  <td className="px-4 py-3 text-right">{a.offered_rate ?? "—"}%</td>
                  <td className="px-4 py-3 text-right capitalize">{a.status.replace("_", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:0.375rem;padding:0.5rem 0.75rem;font-size:0.875rem}`}</style>
    </AppShell>
  );
}

function Slider({ label, value, min, max, step, onChange, format }: any) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</label>
        <span className="text-sm font-semibold">{format(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(+e.target.value)} className="w-full accent-primary" />
    </div>
  );
}

function ResultBox({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-background"}`}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-xl font-semibold ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1">{label}</label>
      {children}
    </div>
  );
}
